export const VISUAL_PRESET_IDS = [
  'neon',
  'retroArcade',
  'pastelDream',
  'storybook',
  'cinematic',
] as const;

export type VisualPresetId = (typeof VISUAL_PRESET_IDS)[number];

export interface ScenePalette {
  readonly background: number;
  readonly fog: number;
  readonly ground: number;
  readonly track: number;
  readonly laneStripe: number;
  readonly rail: number;
  readonly coin: number;
  readonly barrier: number;
  readonly barrierAccent: number;
  readonly beam: number;
  readonly blocker: number;
  readonly blockerInset: number;
  readonly avatarHoodie: number;
  readonly avatarSkin: number;
  readonly avatarDark: number;
  readonly avatarAccent: number;
  readonly shadow: number;
  readonly ambientLight: number;
  readonly keyLight: number;
}

export interface UiPalette {
  readonly page: string;
  readonly text: string;
  readonly muted: string;
  readonly accent: string;
  readonly accentBright: string;
  readonly surface: string;
  readonly border: string;
  readonly buttonText: string;
  readonly glow: string;
}

export interface RingEffectStyle {
  readonly enabled: boolean;
  readonly color: number;
  readonly duration: number;
  readonly startScale: number;
  readonly endScale: number;
  readonly opacity: number;
  readonly drift: number;
}

export interface SlideTrailStyle {
  readonly enabled: boolean;
  readonly color: number;
  readonly duration: number;
  readonly interval: number;
  readonly sideOffset: number;
  readonly width: number;
  readonly length: number;
  readonly opacity: number;
  readonly drift: number;
}

export interface ActionEffectStyle {
  readonly jumpDust: RingEffectStyle;
  readonly landingDust: RingEffectStyle;
  readonly slideTrails: SlideTrailStyle;
}

export interface CameraMotionStyle {
  readonly fovResponsiveness: number;
  readonly laneResponsiveness: number;
  readonly laneReturnResponsiveness: number;
  readonly actionResponsiveness: number;
  readonly laneFollow: number;
  readonly laneLookFollow: number;
  readonly laneRoll: number;
  readonly speedFovBoost: number;
  readonly speedFovRange: number;
  readonly jumpReferenceHeight: number;
  readonly jumpLift: number;
  readonly jumpPullback: number;
  readonly jumpLookLift: number;
  readonly jumpFovNarrowing: number;
  readonly slideDrop: number;
  readonly slidePushIn: number;
  readonly slideLookDrop: number;
  readonly slideFovBoost: number;
  readonly landingDuration: number;
  readonly landingVerticalImpulse: number;
  readonly landingDepthImpulse: number;
  readonly landingLookInfluence: number;
  readonly collisionDuration: number;
  readonly collisionShakeX: number;
  readonly collisionShakeY: number;
  readonly collisionRoll: number;
  readonly collisionFovKick: number;
}

export interface MaterialStyle {
  readonly roughness: number;
  readonly metalness: number;
  readonly flatShading: boolean;
  readonly geometrySegments: 6 | 8 | 12;
}

export interface VisualPreset {
  readonly id: VisualPresetId;
  readonly label: string;
  readonly description: string;
  readonly palette: ScenePalette;
  readonly ui: UiPalette;
  readonly materials: MaterialStyle;
  readonly effects: ActionEffectStyle;
  readonly camera: CameraMotionStyle;
}

function isUnitInterval(value: number): boolean {
  return Number.isFinite(value) && value >= 0 && value <= 1;
}

function isPositive(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}

export function validateVisualPreset(preset: VisualPreset): readonly string[] {
  const errors: string[] = [];

  for (const [name, color] of Object.entries(preset.palette)) {
    if (!Number.isInteger(color) || color < 0 || color > 0xffffff) {
      errors.push(`palette.${name} must be a 24-bit color`);
    }
  }
  for (const name of [
    'page',
    'text',
    'muted',
    'accent',
    'accentBright',
    'surface',
    'border',
    'buttonText',
    'glow',
  ] as const satisfies readonly (keyof UiPalette)[]) {
    if (preset.ui[name].trim() === '') errors.push(`ui.${name} must not be empty`);
  }
  if (!isUnitInterval(preset.materials.roughness)) {
    errors.push('materials.roughness must be between 0 and 1');
  }
  if (!isUnitInterval(preset.materials.metalness)) {
    errors.push('materials.metalness must be between 0 and 1');
  }

  for (const [name, value] of Object.entries(preset.camera)) {
    if (!Number.isFinite(value) || value < 0) {
      errors.push(`camera.${name} must be finite and non-negative`);
    }
  }
  for (const name of [
    'fovResponsiveness',
    'laneResponsiveness',
    'laneReturnResponsiveness',
    'actionResponsiveness',
    'jumpReferenceHeight',
    'landingDuration',
    'collisionDuration',
  ] as const satisfies readonly (keyof CameraMotionStyle)[]) {
    if (preset.camera[name] === 0) errors.push(`camera.${name} must be positive`);
  }

  const rings: readonly (readonly [string, RingEffectStyle])[] = [
    ['jumpDust', preset.effects.jumpDust],
    ['landingDust', preset.effects.landingDust],
  ];
  for (const [name, ring] of rings) {
    if (!isPositive(ring.duration)) errors.push(`effects.${name}.duration must be positive`);
    if (!isPositive(ring.startScale) || !isPositive(ring.endScale)) {
      errors.push(`effects.${name} scales must be positive`);
    }
    if (!isUnitInterval(ring.opacity)) {
      errors.push(`effects.${name}.opacity must be between 0 and 1`);
    }
  }

  const trails = preset.effects.slideTrails;
  for (const [name, value] of [
    ['duration', trails.duration],
    ['interval', trails.interval],
    ['sideOffset', trails.sideOffset],
    ['width', trails.width],
    ['length', trails.length],
  ] as const) {
    if (!isPositive(value)) errors.push(`effects.slideTrails.${name} must be positive`);
  }
  if (!isUnitInterval(trails.opacity)) {
    errors.push('effects.slideTrails.opacity must be between 0 and 1');
  }

  return errors;
}

function defineVisualPreset(preset: VisualPreset): VisualPreset {
  const errors = validateVisualPreset(preset);
  if (errors.length > 0)
    throw new Error(`Invalid visual preset ${preset.id}: ${errors.join('; ')}`);
  Object.freeze(preset.palette);
  Object.freeze(preset.ui);
  Object.freeze(preset.materials);
  Object.freeze(preset.camera);
  Object.freeze(preset.effects.jumpDust);
  Object.freeze(preset.effects.landingDust);
  Object.freeze(preset.effects.slideTrails);
  Object.freeze(preset.effects);
  return Object.freeze(preset);
}

const SHARP_EFFECTS = {
  jumpDust: {
    enabled: true,
    color: 0x67f4c7,
    duration: 0.24,
    startScale: 0.68,
    endScale: 2.9,
    opacity: 0.9,
    drift: 4.2,
  },
  landingDust: {
    enabled: true,
    color: 0xffd35a,
    duration: 0.32,
    startScale: 0.82,
    endScale: 3.7,
    opacity: 0.95,
    drift: 4.8,
  },
  slideTrails: {
    enabled: true,
    color: 0x48e1e8,
    duration: 0.24,
    interval: 0.55,
    sideOffset: 0.42,
    width: 0.11,
    length: 1.55,
    opacity: 0.86,
    drift: 5.8,
  },
} as const satisfies ActionEffectStyle;

const ARCADE_CAMERA = {
  fovResponsiveness: 7.5,
  laneResponsiveness: 10,
  laneReturnResponsiveness: 8,
  actionResponsiveness: 11,
  laneFollow: 0.05,
  laneLookFollow: 0.06,
  laneRoll: 0,
  speedFovBoost: 4.5,
  speedFovRange: 8.5,
  jumpReferenceHeight: 1.4,
  jumpLift: 0.26,
  jumpPullback: 0.14,
  jumpLookLift: 0.13,
  jumpFovNarrowing: 0.8,
  slideDrop: 0.16,
  slidePushIn: 0.2,
  slideLookDrop: 0.1,
  slideFovBoost: 1.8,
  landingDuration: 0.36,
  landingVerticalImpulse: 0.17,
  landingDepthImpulse: 0.13,
  landingLookInfluence: 0.25,
  collisionDuration: 0.42,
  collisionShakeX: 0.32,
  collisionShakeY: 0.17,
  collisionRoll: 0.055,
  collisionFovKick: 5.5,
} as const satisfies CameraMotionStyle;

export const VISUAL_PRESETS = Object.freeze({
  neon: defineVisualPreset({
    id: 'neon',
    label: 'Neon Sprint',
    description: 'The original teal night track, polished with crisp action trails.',
    palette: {
      background: 0x091b24,
      fog: 0x091b24,
      ground: 0x0b2931,
      track: 0x164654,
      laneStripe: 0x9ee8dd,
      rail: 0x25d6a2,
      coin: 0xffd35a,
      barrier: 0xff8a42,
      barrierAccent: 0xffe07b,
      beam: 0x48e1e8,
      blocker: 0xf2495c,
      blockerInset: 0x641e35,
      avatarHoodie: 0x25d6a2,
      avatarSkin: 0xffc7a8,
      avatarDark: 0x102b35,
      avatarAccent: 0xffd35a,
      shadow: 0x020b0e,
      ambientLight: 0xcdeeff,
      keyLight: 0xfff0cf,
    },
    ui: {
      page: '#06141b',
      text: '#f4ffff',
      muted: '#b9d8d4',
      accent: '#25d6a2',
      accentBright: '#67f4c7',
      surface: 'rgba(4, 18, 24, 0.72)',
      border: 'rgba(158, 232, 221, 0.28)',
      buttonText: '#052019',
      glow: 'rgba(37, 214, 162, 0.42)',
    },
    materials: { roughness: 0.72, metalness: 0.04, flatShading: false, geometrySegments: 12 },
    effects: SHARP_EFFECTS,
    camera: ARCADE_CAMERA,
  }),
  retroArcade: defineVisualPreset({
    id: 'retroArcade',
    label: 'Retro Arcade',
    description: 'Chunky low-poly geometry with saturated cabinet-era purple and cyan.',
    palette: {
      background: 0x160d2b,
      fog: 0x160d2b,
      ground: 0x24133f,
      track: 0x3b2463,
      laneStripe: 0x52f7ff,
      rail: 0xff4fd8,
      coin: 0xfff65c,
      barrier: 0xff7438,
      barrierAccent: 0xfff65c,
      beam: 0x52f7ff,
      blocker: 0xff3d81,
      blockerInset: 0x63164f,
      avatarHoodie: 0x52f7ff,
      avatarSkin: 0xffbd9f,
      avatarDark: 0x24133f,
      avatarAccent: 0xfff65c,
      shadow: 0x08020f,
      ambientLight: 0xbba9ff,
      keyLight: 0xffb7ed,
    },
    ui: {
      page: '#10081f',
      text: '#fffaff',
      muted: '#c9b9dd',
      accent: '#ff4fd8',
      accentBright: '#52f7ff',
      surface: 'rgba(22, 8, 43, 0.82)',
      border: 'rgba(82, 247, 255, 0.34)',
      buttonText: '#160d2b',
      glow: 'rgba(255, 79, 216, 0.5)',
    },
    materials: { roughness: 0.58, metalness: 0.08, flatShading: true, geometrySegments: 6 },
    effects: {
      ...SHARP_EFFECTS,
      jumpDust: { ...SHARP_EFFECTS.jumpDust, color: 0xff4fd8, endScale: 2.5 },
      landingDust: { ...SHARP_EFFECTS.landingDust, color: 0xfff65c },
      slideTrails: { ...SHARP_EFFECTS.slideTrails, color: 0x52f7ff, width: 0.11 },
    },
    camera: ARCADE_CAMERA,
  }),
  pastelDream: defineVisualPreset({
    id: 'pastelDream',
    label: 'Pastel Dream',
    description: 'Soft lavender, peach, and mint for a playful gift-card feel.',
    palette: {
      background: 0xb9b4e8,
      fog: 0xcac5ee,
      ground: 0xb0d9cd,
      track: 0xe4d6dd,
      laneStripe: 0xfff7e8,
      rail: 0x7bcdb4,
      coin: 0xffc968,
      barrier: 0xf09a8a,
      barrierAccent: 0xfff0c7,
      beam: 0x80bfd5,
      blocker: 0xb181b8,
      blockerInset: 0x725c91,
      avatarHoodie: 0x7bcdb4,
      avatarSkin: 0xf2bca4,
      avatarDark: 0x554968,
      avatarAccent: 0xffd477,
      shadow: 0x766f91,
      ambientLight: 0xfff8f0,
      keyLight: 0xffe6c7,
    },
    ui: {
      page: '#8f89bd',
      text: '#fffdf8',
      muted: '#eee7f0',
      accent: '#7bcdb4',
      accentBright: '#fff0c7',
      surface: 'rgba(83, 73, 104, 0.72)',
      border: 'rgba(255, 247, 232, 0.42)',
      buttonText: '#493f59',
      glow: 'rgba(255, 202, 204, 0.48)',
    },
    materials: { roughness: 0.86, metalness: 0, flatShading: false, geometrySegments: 12 },
    effects: {
      jumpDust: { ...SHARP_EFFECTS.jumpDust, color: 0xfff0c7, duration: 0.5, opacity: 0.48 },
      landingDust: {
        ...SHARP_EFFECTS.landingDust,
        color: 0xf6b6c6,
        duration: 0.62,
        opacity: 0.55,
      },
      slideTrails: {
        ...SHARP_EFFECTS.slideTrails,
        color: 0xfff0c7,
        duration: 0.4,
        opacity: 0.44,
      },
    },
    camera: ARCADE_CAMERA,
  }),
  storybook: defineVisualPreset({
    id: 'storybook',
    label: 'Storybook',
    description: 'Warm paper, forest green, and terracotta with a handcrafted low-poly finish.',
    palette: {
      background: 0x9ebc9f,
      fog: 0xaec5a6,
      ground: 0x68866b,
      track: 0xb99d75,
      laneStripe: 0xf4e5c1,
      rail: 0x365a48,
      coin: 0xe2b64f,
      barrier: 0xbd654a,
      barrierAccent: 0xf0cf82,
      beam: 0x4d7a66,
      blocker: 0x8e493c,
      blockerInset: 0x56372f,
      avatarHoodie: 0xd47752,
      avatarSkin: 0xdba987,
      avatarDark: 0x3d302d,
      avatarAccent: 0xf0cf82,
      shadow: 0x334b3b,
      ambientLight: 0xffefd2,
      keyLight: 0xffd998,
    },
    ui: {
      page: '#294437',
      text: '#fff3d8',
      muted: '#d8d3b6',
      accent: '#d47752',
      accentBright: '#f0cf82',
      surface: 'rgba(42, 55, 43, 0.78)',
      border: 'rgba(240, 207, 130, 0.34)',
      buttonText: '#3d302d',
      glow: 'rgba(212, 119, 82, 0.4)',
    },
    materials: { roughness: 0.94, metalness: 0, flatShading: true, geometrySegments: 8 },
    effects: {
      jumpDust: { ...SHARP_EFFECTS.jumpDust, color: 0xf4e5c1, opacity: 0.5 },
      landingDust: { ...SHARP_EFFECTS.landingDust, color: 0xd8b27a, opacity: 0.62 },
      slideTrails: { ...SHARP_EFFECTS.slideTrails, color: 0xf4e5c1, opacity: 0.38 },
    },
    camera: ARCADE_CAMERA,
  }),
  cinematic: defineVisualPreset({
    id: 'cinematic',
    label: 'Cinematic',
    description: 'Grounded asphalt and safety colors for testing a more realistic direction.',
    palette: {
      background: 0x172027,
      fog: 0x202c34,
      ground: 0x263239,
      track: 0x343d42,
      laneStripe: 0xc4c6bf,
      rail: 0x626e73,
      coin: 0xe9b949,
      barrier: 0xd96732,
      barrierAccent: 0xf1cf72,
      beam: 0x7d9299,
      blocker: 0x9f3434,
      blockerInset: 0x411f22,
      avatarHoodie: 0x345f70,
      avatarSkin: 0xc88f70,
      avatarDark: 0x1b2226,
      avatarAccent: 0xd9aa43,
      shadow: 0x080b0d,
      ambientLight: 0xb7c6cf,
      keyLight: 0xffd3a0,
    },
    ui: {
      page: '#11181d',
      text: '#f2f3ef',
      muted: '#b3bdc0',
      accent: '#d96732',
      accentBright: '#f1cf72',
      surface: 'rgba(14, 20, 24, 0.82)',
      border: 'rgba(196, 198, 191, 0.25)',
      buttonText: '#1c1c19',
      glow: 'rgba(217, 103, 50, 0.34)',
    },
    materials: { roughness: 0.82, metalness: 0.12, flatShading: false, geometrySegments: 12 },
    effects: {
      jumpDust: { ...SHARP_EFFECTS.jumpDust, color: 0xa99d84, opacity: 0.3 },
      landingDust: { ...SHARP_EFFECTS.landingDust, color: 0xb1a283, opacity: 0.42 },
      slideTrails: { ...SHARP_EFFECTS.slideTrails, color: 0xc6b892, opacity: 0.28 },
    },
    camera: ARCADE_CAMERA,
  }),
} as const satisfies Readonly<Record<VisualPresetId, VisualPreset>>);

export const DEFAULT_VISUAL_PRESET_ID: VisualPresetId = 'neon';

export function isVisualPresetId(value: string): value is VisualPresetId {
  return VISUAL_PRESET_IDS.some((id) => id === value);
}

export function getVisualPreset(requested: string | null | undefined): VisualPreset {
  return requested !== undefined && requested !== null && isVisualPresetId(requested)
    ? VISUAL_PRESETS[requested]
    : VISUAL_PRESETS[DEFAULT_VISUAL_PRESET_ID];
}

export function applyVisualPreset(element: HTMLElement, preset: VisualPreset): void {
  element.dataset.visualPreset = preset.id;
  element.style.setProperty('--ui-page', preset.ui.page);
  element.style.setProperty('--ui-text', preset.ui.text);
  element.style.setProperty('--ui-muted', preset.ui.muted);
  element.style.setProperty('--ui-accent', preset.ui.accent);
  element.style.setProperty('--ui-accent-bright', preset.ui.accentBright);
  element.style.setProperty('--ui-surface', preset.ui.surface);
  element.style.setProperty('--ui-border', preset.ui.border);
  element.style.setProperty('--ui-button-text', preset.ui.buttonText);
  element.style.setProperty('--ui-glow', preset.ui.glow);
}
